import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, FileText, MessageSquare, LogOut, Shield,
  Clock, CheckCircle2, AlertCircle, Send, Loader2,
  Mail, Phone, MapPin, Calendar, Search, Filter,
  X, PlayCircle, ChevronRight, Pencil, Trash2, KeyRound, Copy,
  DollarSign, Home, FileCheck, Banknote, CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  
  // Filter and search state
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // User management state
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ name: '', email: '', phone: '', address: '' });
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  
  // Quote form state
  const [quoteForm, setQuoteForm] = useState({
    quoted_amount: '',
    job_address: '',
    expected_completion_date: '',
    job_details: ''
  });
  const [isSettingQuote, setIsSettingQuote] = useState(false);
  const [isCancellingRequest, setIsCancellingRequest] = useState(null);
  
  // Security/Login Activity state
  const [loginActivity, setLoginActivity] = useState([]);
  const [loginActivityStats, setLoginActivityStats] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all');

  const getAuthHeader = () => {
    const token = localStorage.getItem('ach_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const token = localStorage.getItem('ach_admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    try {
      const headers = getAuthHeader();
      const [statsRes, usersRes, quotesRes, messagesRes, activityRes, activityStatsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/quotes`, { headers }),
        axios.get(`${API}/admin/messages`, { headers }),
        axios.get(`${API}/admin/login-activity?limit=50`, { headers }),
        axios.get(`${API}/admin/login-activity/stats`, { headers })
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setQuotes(quotesRes.data);
      setMessages(messagesRes.data);
      setLoginActivity(activityRes.data);
      setLoginActivityStats(activityStatsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ach_admin_token');
    localStorage.removeItem('ach_admin_user');
    navigate('/admin');
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/quotes/${quoteId}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success('Status updated');
      fetchAllData();
      
      // Update selected request if open
      if (selectedRequest && selectedRequest.id === quoteId) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleReplyMessage = async () => {
    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    setIsSendingReply(true);
    try {
      await axios.post(
        `${API}/admin/messages/${selectedMessage.id}/reply`,
        { reply: replyContent },
        { headers: getAuthHeader() }
      );
      toast.success('Reply sent successfully');
      setSelectedMessage(null);
      setReplyContent('');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setIsSendingReply(false);
    }
  };

  // User management functions
  const openEditUser = (user) => {
    setSelectedUser(user);
    setEditUserData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      address: user.address || ''
    });
    setIsEditingUser(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}`,
        editUserData,
        { headers: getAuthHeader() }
      );
      toast.success('User updated successfully');
      setIsEditingUser(false);
      setSelectedUser(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeader() });
      toast.success('User deleted successfully');
      setShowDeleteConfirm(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await axios.post(
        `${API}/admin/users/${userId}/reset-password`,
        {},
        { headers: getAuthHeader() }
      );
      setResetPasswordResult({
        userId,
        tempPassword: response.data.temp_password,
        email: response.data.user_email
      });
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Handle setting quote details
  const handleSetQuote = async () => {
    if (!selectedRequest) return;
    
    if (!quoteForm.quoted_amount || !quoteForm.job_address || !quoteForm.expected_completion_date) {
      toast.error('Please fill in Amount, Job Address, and Expected Completion Date');
      return;
    }
    
    setIsSettingQuote(true);
    try {
      await axios.put(
        `${API}/admin/quotes/${selectedRequest.id}/set-quote`,
        {
          quoted_amount: parseFloat(quoteForm.quoted_amount),
          job_address: quoteForm.job_address,
          expected_completion_date: quoteForm.expected_completion_date,
          job_details: quoteForm.job_details
        },
        { headers: getAuthHeader() }
      );
      toast.success('Quote sent to customer! They will receive an email notification.');
      setSelectedRequest(null);
      setQuoteForm({ quoted_amount: '', job_address: '', expected_completion_date: '', job_details: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set quote');
    } finally {
      setIsSettingQuote(false);
    }
  };

  // Handle cancelling a request
  const handleCancelRequest = async (quoteId) => {
    setIsCancellingRequest(quoteId);
    try {
      await axios.post(
        `${API}/admin/quotes/${quoteId}/cancel`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Request cancelled successfully');
      setSelectedRequest(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    } finally {
      setIsCancellingRequest(null);
    }
  };

  // Switch from cash to card payment
  const handleSwitchToCard = async (quoteId) => {
    try {
      await axios.post(
        `${API}/admin/quotes/${quoteId}/switch-to-card`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Switched to card payment. Customer can now pay online.');
      setSelectedRequest({ ...selectedRequest, payment_status: 'pending' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch payment method');
    }
  };

  // Open request and pre-fill quote form
  const openRequestDetail = (quote) => {
    setSelectedRequest(quote);
    setQuoteForm({
      quoted_amount: quote.quoted_amount?.toString() || '',
      job_address: quote.job_address || quote.property_address || '',
      expected_completion_date: quote.expected_completion_date || quote.completion_date || quote.requested_date || '',
      job_details: quote.request_details || quote.message || ''
    });
  };

  // Filter users by search
  const filteredUsers = users.filter(user => {
    if (!userSearchQuery) return true;
    const query = userSearchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.phone?.includes(query)
    );
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Pending' },
      'in-progress': { icon: PlayCircle, color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
      complete: { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Complete' },
      cancelled: { icon: X, color: 'bg-red-100 text-red-700', label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter and search quotes
  const filteredQuotes = quotes.filter(quote => {
    // Status filter
    if (statusFilter !== 'all' && quote.status !== statusFilter) {
      return false;
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const searchFields = [
        quote.name,
        quote.email,
        quote.phone,
        quote.service_type,
        quote.property_type,
        quote.request_details,
        quote.message
      ].filter(Boolean).map(f => f.toLowerCase());
      
      return searchFields.some(field => field.includes(query));
    }
    
    return true;
  });

  // Get status counts from stats (backend) for accurate display
  const statusCounts = stats ? {
    all: stats.total_quotes,
    pending: stats.pending_quotes,
    'in-progress': stats.in_progress_quotes || 0,
    complete: stats.complete_quotes || 0,
    cancelled: stats.cancelled_quotes || 0
  } : {
    all: quotes.length,
    pending: quotes.filter(q => q.status === 'pending').length,
    'in-progress': quotes.filter(q => q.status === 'in-progress').length,
    complete: quotes.filter(q => q.status === 'complete').length,
    cancelled: quotes.filter(q => q.status === 'cancelled').length
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6" />
            <span className="font-heading font-bold text-lg">ACH Admin Portal</span>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-white hover:bg-slate-800"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl text-slate-900">{stats.total_users}</p>
                  <p className="font-body text-xs text-slate-500">Total Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl text-slate-900">{stats.total_quotes}</p>
                  <p className="font-body text-xs text-slate-500">Total Requests</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl text-slate-900">{stats.pending_quotes}</p>
                  <p className="font-body text-xs text-slate-500">Pending</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <PlayCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl text-slate-900">{stats.in_progress_quotes || 0}</p>
                  <p className="font-body text-xs text-slate-500">In Progress</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl text-slate-900">{stats.complete_quotes || 0}</p>
                  <p className="font-body text-xs text-slate-500">Complete</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="requests" className="w-full">
          <TabsList className="bg-white rounded-xl p-1 shadow-sm mb-6">
            <TabsTrigger 
              value="requests" 
              className="font-body text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-lg px-6"
              data-testid="admin-tab-requests"
            >
              <FileText className="w-4 h-4 mr-2" />
              Requests ({quotes.length})
            </TabsTrigger>
            <TabsTrigger 
              value="users" 
              className="font-body text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-lg px-6"
              data-testid="admin-tab-users"
            >
              <Users className="w-4 h-4 mr-2" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger 
              value="messages" 
              className="font-body text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-lg px-6"
              data-testid="admin-tab-messages"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Messages ({messages.length})
            </TabsTrigger>
            <TabsTrigger 
              value="security" 
              className="font-body text-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-lg px-6"
              data-testid="admin-tab-security"
            >
              <Shield className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
          </TabsList>

          {/* Requests Tab */}
          <TabsContent value="requests" className="mt-0">
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {/* Filter and Search Bar */}
              <div className="p-4 border-b border-slate-100">
                <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
                  <h2 className="font-heading font-semibold text-lg text-slate-900">All Quote Requests</h2>
                  
                  <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    {/* Search */}
                    <div className="relative flex-grow sm:flex-grow-0">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="Search name, email, keyword..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-full sm:w-64"
                        data-testid="search-requests-input"
                      />
                    </div>
                    
                    {/* Status Filter */}
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-slate-400" />
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-44" data-testid="status-filter">
                          <SelectValue placeholder="Filter by status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All ({statusCounts.all})</SelectItem>
                          <SelectItem value="pending">Pending ({statusCounts.pending})</SelectItem>
                          <SelectItem value="in-progress">In Progress ({statusCounts['in-progress']})</SelectItem>
                          <SelectItem value="complete">Complete ({statusCounts.complete})</SelectItem>
                          <SelectItem value="cancelled">Cancelled ({statusCounts.cancelled})</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Requests List */}
              <div className="divide-y divide-slate-100">
                {filteredQuotes.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="font-body text-slate-500">
                      {searchQuery || statusFilter !== 'all' 
                        ? 'No requests match your filters' 
                        : 'No requests yet'}
                    </p>
                  </div>
                ) : (
                  filteredQuotes.map((quote) => (
                    <div 
                      key={quote.id}
                      onClick={() => openRequestDetail(quote)}
                      className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                      data-testid={`admin-quote-${quote.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div className="flex-grow">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-body font-semibold text-slate-900">{quote.name}</span>
                            {getStatusBadge(quote.status)}
                            {quote.payment_status === 'paid' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                <CheckCircle2 className="w-3 h-3" />
                                Paid
                              </span>
                            )}
                            {quote.payment_status === 'cash' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                <Banknote className="w-3 h-3" />
                                Cash
                              </span>
                            )}
                            {quote.quoted_amount && quote.payment_status !== 'paid' && quote.payment_status !== 'cash' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                <DollarSign className="w-3 h-3" />
                                ${quote.quoted_amount.toFixed(2)}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {quote.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {quote.phone}
                            </span>
                            <span className="capitalize">{quote.service_type?.replace('-', ' ')}</span>
                            {quote.requested_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {quote.requested_date}
                              </span>
                            )}
                          </div>
                          {quote.request_details && (
                            <p className="mt-2 text-sm text-slate-600 line-clamp-1">
                              {quote.request_details}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-400">{formatDate(quote.created_at)}</span>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-0">
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="font-heading font-semibold text-lg text-slate-900">Registered Members</h2>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search users..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-users-input"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-body text-xs font-medium text-slate-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left font-body text-xs font-medium text-slate-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left font-body text-xs font-medium text-slate-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left font-body text-xs font-medium text-slate-500 uppercase">Joined</th>
                      <th className="px-4 py-3 text-right font-body text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-slate-50" data-testid={`admin-user-${user.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center">
                              <span className="font-heading font-bold text-xs text-white">
                                {user.name?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <p className="font-body font-medium text-sm text-slate-900">{user.name}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-body text-sm text-slate-600">{user.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-body text-sm text-slate-600">{user.phone}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-body text-xs text-slate-500">{formatDate(user.created_at)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditUser(user)}
                              className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                              title="Edit User"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetPassword(user.id)}
                              className="h-8 w-8 p-0 text-slate-500 hover:text-amber-600 hover:bg-amber-50"
                              title="Reset Password"
                              data-testid={`reset-password-${user.id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowDeleteConfirm(user)}
                              className="h-8 w-8 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
                              title="Delete User"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="font-body text-slate-500">
                      {userSearchQuery ? 'No users match your search' : 'No registered users yet'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Messages List */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h2 className="font-heading font-semibold text-lg text-slate-900">Customer Messages</h2>
                </div>
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {messages.map((msg) => (
                    <div 
                      key={msg.id}
                      onClick={() => setSelectedMessage(msg)}
                      className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedMessage?.id === msg.id ? 'bg-blue-50' : ''
                      } ${!msg.is_read ? 'border-l-4 border-blue-500' : ''}`}
                      data-testid={`admin-message-${msg.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-body font-medium text-sm text-slate-900">{msg.user_name}</p>
                          <p className="font-body text-xs text-slate-500">{msg.user_email}</p>
                        </div>
                        <span className="font-body text-xs text-slate-400">
                          {formatDate(msg.created_at)}
                        </span>
                      </div>
                      <p className="font-body font-medium text-sm text-slate-700 mb-1">{msg.subject}</p>
                      <p className="font-body text-xs text-slate-500 truncate">{msg.content}</p>
                      {msg.admin_reply && (
                        <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                          <CheckCircle2 className="w-3 h-3" />
                          Replied
                        </span>
                      )}
                    </div>
                  ))}
                  {messages.length === 0 && (
                    <div className="text-center py-12">
                      <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="font-body text-slate-500">No messages yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Message Detail & Reply */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {selectedMessage ? (
                  <div className="p-6">
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center">
                          <span className="font-heading font-bold text-sm text-white">
                            {selectedMessage.user_name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-body font-medium text-slate-900">{selectedMessage.user_name}</p>
                          <p className="font-body text-xs text-slate-500">{selectedMessage.user_email}</p>
                        </div>
                      </div>
                      <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                        {selectedMessage.subject}
                      </h3>
                      <p className="font-body text-slate-600 whitespace-pre-wrap">
                        {selectedMessage.content}
                      </p>
                      <p className="font-body text-xs text-slate-400 mt-2">
                        Received: {formatDate(selectedMessage.created_at)}
                      </p>
                    </div>

                    {selectedMessage.admin_reply ? (
                      <div className="bg-green-50 rounded-xl p-4 mb-4">
                        <p className="font-body text-xs text-green-600 font-medium mb-2">Your Reply:</p>
                        <p className="font-body text-slate-700">{selectedMessage.admin_reply}</p>
                        <p className="font-body text-xs text-slate-400 mt-2">
                          Sent: {selectedMessage.replied_at ? formatDate(selectedMessage.replied_at) : 'N/A'}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="font-body text-sm text-slate-600 mb-2 block">Reply to customer:</label>
                        <Textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Type your reply..."
                          rows={4}
                          className="mb-4 resize-none"
                          data-testid="admin-reply-input"
                        />
                        <Button
                          onClick={handleReplyMessage}
                          disabled={isSendingReply}
                          className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                          data-testid="admin-send-reply-btn"
                        >
                          {isSendingReply ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Reply
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full min-h-[400px]">
                    <div className="text-center">
                      <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="font-body text-slate-500">Select a message to view details</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="mt-0">
            <div className="space-y-6">
              {/* Security Stats Cards */}
              {loginActivityStats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-body text-xs text-slate-500 uppercase tracking-wider">Login Attempts (24h)</p>
                        <p className="font-heading text-2xl font-bold text-slate-900 mt-1">{loginActivityStats.total_attempts_24h}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                        <Shield className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-body text-xs text-slate-500 uppercase tracking-wider">Failed Attempts (24h)</p>
                        <p className="font-heading text-2xl font-bold text-red-600 mt-1">{loginActivityStats.failed_attempts_24h}</p>
                      </div>
                      <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-red-600" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-body text-xs text-slate-500 uppercase tracking-wider">Admin Failed</p>
                        <p className="font-heading text-2xl font-bold text-orange-600 mt-1">{loginActivityStats.admin_failed_24h}</p>
                      </div>
                      <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                        <KeyRound className="w-6 h-6 text-orange-600" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-body text-xs text-slate-500 uppercase tracking-wider">Suspicious IPs</p>
                        <p className="font-heading text-2xl font-bold text-purple-600 mt-1">{loginActivityStats.suspicious_ips}</p>
                      </div>
                      <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">IPs with 3+ failed attempts</p>
                  </div>
                </div>
              )}

              {/* Login Activity Table */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <h2 className="font-heading font-semibold text-lg text-slate-900">Login Activity</h2>
                    <div className="flex gap-3">
                      <Select value={activityFilter} onValueChange={setActivityFilter}>
                        <SelectTrigger className="w-[150px]" data-testid="activity-filter">
                          <Filter className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Activity</SelectItem>
                          <SelectItem value="failed">Failed Only</SelectItem>
                          <SelectItem value="success">Success Only</SelectItem>
                          <SelectItem value="admin">Admin Only</SelectItem>
                          <SelectItem value="user">Users Only</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={fetchAllData}
                        data-testid="refresh-activity"
                      >
                        Refresh
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left font-body text-xs text-slate-500 uppercase tracking-wider px-4 py-3">Time</th>
                        <th className="text-left font-body text-xs text-slate-500 uppercase tracking-wider px-4 py-3">Type</th>
                        <th className="text-left font-body text-xs text-slate-500 uppercase tracking-wider px-4 py-3">User</th>
                        <th className="text-left font-body text-xs text-slate-500 uppercase tracking-wider px-4 py-3">IP Address</th>
                        <th className="text-left font-body text-xs text-slate-500 uppercase tracking-wider px-4 py-3">Status</th>
                        <th className="text-left font-body text-xs text-slate-500 uppercase tracking-wider px-4 py-3">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {loginActivity
                        .filter(activity => {
                          if (activityFilter === 'all') return true;
                          if (activityFilter === 'failed') return !activity.success;
                          if (activityFilter === 'success') return activity.success;
                          if (activityFilter === 'admin') return activity.user_type === 'admin';
                          if (activityFilter === 'user') return activity.user_type === 'user';
                          return true;
                        })
                        .map((activity, index) => (
                        <tr key={activity.id || index} className={`hover:bg-slate-50 ${!activity.success ? 'bg-red-50/30' : ''}`}>
                          <td className="px-4 py-3">
                            <span className="font-body text-sm text-slate-600">
                              {new Date(activity.created_at).toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              activity.user_type === 'admin' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {activity.user_type === 'admin' ? 'Admin' : 'User'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-body text-sm text-slate-900 font-medium">
                              {activity.identifier}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                              {activity.ip_address}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {activity.success ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                <CheckCircle2 className="w-3 h-3" />
                                Success
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                <AlertCircle className="w-3 h-3" />
                                Failed
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-body text-xs text-slate-500">
                              {activity.failure_reason || '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {loginActivity.length === 0 && (
                  <div className="text-center py-12">
                    <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="font-body text-slate-500">No login activity recorded yet</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Request Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedRequest(null)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex justify-between items-center z-10">
              <div className="flex items-center gap-3">
                <h2 className="font-heading font-bold text-xl text-slate-900">Request Details</h2>
                {selectedRequest.payment_status === 'paid' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    <CheckCircle2 className="w-3 h-3" />
                    Paid
                  </span>
                )}
                {selectedRequest.payment_status === 'cash' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                    <Banknote className="w-3 h-3" />
                    Cash
                  </span>
                )}
              </div>
              <button 
                onClick={() => setSelectedRequest(null)}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                data-testid="close-request-modal"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Status Section */}
              <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="font-body text-xs text-slate-500 uppercase tracking-wider mb-1">Current Status</p>
                    {getStatusBadge(selectedRequest.status)}
                    {selectedRequest.payment_status === 'declined' && (
                      <span className="ml-2 inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                        Quote Declined - Can Re-quote
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Status dropdown - now available for all quotes including paid ones */}
                    <div>
                      <p className="font-body text-xs text-slate-500 mb-1">Change Status:</p>
                      <Select 
                        value={selectedRequest.status} 
                        onValueChange={(value) => handleStatusChange(selectedRequest.id, value)}
                      >
                        <SelectTrigger className="w-44" data-testid="modal-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">
                            <span className="flex items-center gap-2">
                              <Clock className="w-3 h-3 text-amber-500" />
                              Pending
                            </span>
                          </SelectItem>
                          <SelectItem value="in-progress">
                            <span className="flex items-center gap-2">
                              <PlayCircle className="w-3 h-3 text-blue-500" />
                              In Progress
                            </span>
                          </SelectItem>
                          <SelectItem value="complete">
                            <span className="flex items-center gap-2">
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                              Complete
                            </span>
                          </SelectItem>
                          <SelectItem value="cancelled">
                            <span className="flex items-center gap-2">
                              <X className="w-3 h-3 text-red-500" />
                              Cancelled
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedRequest.payment_status !== 'paid' && selectedRequest.status !== 'cancelled' && (
                          <Button
                            onClick={() => handleCancelRequest(selectedRequest.id)}
                            disabled={isCancellingRequest === selectedRequest.id}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50 font-body text-xs"
                            data-testid="cancel-request-btn"
                          >
                            {isCancellingRequest === selectedRequest.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <X className="w-3 h-3 mr-1" />
                                Cancel Request
                              </>
                            )}
                          </Button>
                        )}
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="mb-6">
                <h3 className="font-heading font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">
                  Customer Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Users className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-body text-xs text-slate-500">Name</p>
                      <p className="font-body font-medium text-slate-900">{selectedRequest.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-body text-xs text-slate-500">Email</p>
                      <p className="font-body font-medium text-slate-900">{selectedRequest.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-body text-xs text-slate-500">Phone</p>
                      <p className="font-body font-medium text-slate-900">{selectedRequest.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-body text-xs text-slate-500">Requested Date</p>
                      <p className="font-body font-medium text-slate-900">
                        {selectedRequest.requested_date || 'Not specified'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Info */}
              <div className="mb-6">
                <h3 className="font-heading font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">
                  Service Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-body text-xs text-slate-500 mb-1">Service Type</p>
                    <p className="font-body font-medium text-slate-900 capitalize">
                      {selectedRequest.service_type?.replace('-', ' ')}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-body text-xs text-slate-500 mb-1">Property Type</p>
                    <p className="font-body font-medium text-slate-900 capitalize">
                      {selectedRequest.property_type?.replace('-', ' ') || 'Not specified'}
                    </p>
                  </div>
                </div>
                {selectedRequest.property_address && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                    <p className="font-body text-xs text-slate-500 mb-1">Property Address</p>
                    <p className="font-body font-medium text-slate-900">
                      {selectedRequest.property_address}
                    </p>
                  </div>
                )}
              </div>

              {/* Request Details */}
              {selectedRequest.request_details && (
                <div className="mb-6">
                  <h3 className="font-heading font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">
                    Request Specifics
                  </h3>
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <p className="font-body text-slate-700 whitespace-pre-wrap">
                      {selectedRequest.request_details}
                    </p>
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              {selectedRequest.message && (
                <div className="mb-6">
                  <h3 className="font-heading font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">
                    Additional Notes
                  </h3>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="font-body text-slate-700 whitespace-pre-wrap">
                      {selectedRequest.message}
                    </p>
                  </div>
                </div>
              )}

              {/* Existing Quote Info (if already quoted) */}
              {selectedRequest.quoted_amount && (
                <div className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
                  <h3 className="font-heading font-semibold text-sm text-green-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <FileCheck className="w-4 h-4" />
                    Quote Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-body text-xs text-green-700">Quoted Amount</p>
                      <p className="font-heading font-bold text-2xl text-green-800">${selectedRequest.quoted_amount.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-green-700">Expected Completion Date</p>
                      <p className="font-body font-medium text-green-800">{selectedRequest.expected_completion_date || selectedRequest.completion_date || 'TBD'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="font-body text-xs text-green-700">Job Address</p>
                      <p className="font-body font-medium text-green-800">{selectedRequest.job_address || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-green-200 flex items-center justify-between">
                    <p className="font-body text-xs text-green-700">
                      Payment Status: <span className="font-semibold">
                        {selectedRequest.payment_status === 'paid' 
                          ? `Paid${selectedRequest.transaction_id ? ` (ID: ${selectedRequest.transaction_id.slice(-8)})` : ''}` 
                          : selectedRequest.payment_status === 'cash'
                            ? 'Cash Payment'
                            : selectedRequest.payment_status === 'declined'
                              ? 'Declined by Customer'
                              : 'Awaiting Payment'}
                      </span>
                    </p>
                    {selectedRequest.payment_status === 'cash' && (
                      <Button
                        onClick={() => handleSwitchToCard(selectedRequest.id)}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50 font-body text-xs"
                        data-testid="switch-to-card-btn"
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        Switch to Card
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Quote Form (show when pending without a quote, OR when quote was declined) */}
              {((selectedRequest.status === 'pending' && !selectedRequest.quoted_amount) || 
                selectedRequest.payment_status === 'declined') && (
                <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h3 className="font-heading font-semibold text-sm text-amber-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    {selectedRequest.payment_status === 'declined' ? 'Revise Quote & Send to Customer' : 'Set Quote & Send to Customer'}
                  </h3>
                  {selectedRequest.payment_status === 'declined' && (
                    <div className="bg-orange-100 rounded-lg p-3 mb-4">
                      <p className="font-body text-xs text-orange-800">
                        <strong>Note:</strong> The customer declined the previous quote. Please provide a revised quote.
                      </p>
                    </div>
                  )}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-body text-sm text-amber-800">Quote Amount ($) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={quoteForm.quoted_amount}
                          onChange={(e) => setQuoteForm({ ...quoteForm, quoted_amount: e.target.value })}
                          placeholder="0.00"
                          className="font-body"
                          data-testid="quote-amount-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-body text-sm text-amber-800">Expected Completion Date *</Label>
                        <Input
                          type="date"
                          value={quoteForm.expected_completion_date}
                          onChange={(e) => setQuoteForm({ ...quoteForm, expected_completion_date: e.target.value })}
                          className="font-body"
                          data-testid="quote-date-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body text-sm text-amber-800">Job Address *</Label>
                      <Input
                        value={quoteForm.job_address}
                        onChange={(e) => setQuoteForm({ ...quoteForm, job_address: e.target.value })}
                        placeholder="Enter the job address"
                        className="font-body"
                        data-testid="quote-address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body text-sm text-amber-800">Job Details</Label>
                      <Textarea
                        value={quoteForm.job_details}
                        onChange={(e) => setQuoteForm({ ...quoteForm, job_details: e.target.value })}
                        placeholder="Describe the work to be performed..."
                        rows={3}
                        className="font-body resize-none"
                        data-testid="quote-details-input"
                      />
                    </div>
                    <div className="bg-amber-100 rounded-lg p-3">
                      <p className="font-body text-xs text-amber-800">
                        <strong>Disclaimer (Auto-included):</strong> All sales are final. An inspection by the requestor at time of completion is requested. If the party is not on site, ACH Cleaning Co is not liable for any issues missed by the requesting party. Any further cleaning will incur another charge. ONLY ACH Cleaning Co administration can authorize any fee reduction or charge off.
                      </p>
                    </div>
                    <Button
                      onClick={handleSetQuote}
                      disabled={isSettingQuote}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white font-body font-medium rounded-full"
                      data-testid="send-quote-btn"
                    >
                      {isSettingQuote ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {selectedRequest.payment_status === 'declined' ? 'Send Revised Quote' : 'Send Quote to Customer'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Submission Info */}
              <div className="pt-4 border-t border-slate-100">
                <p className="font-body text-xs text-slate-400">
                  Submitted on {formatDate(selectedRequest.created_at)}
                  {selectedRequest.user_id && ' • Registered Member'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditingUser && selectedUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setIsEditingUser(false)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
              <h2 className="font-heading font-bold text-lg text-slate-900">Edit User</h2>
              <button 
                onClick={() => setIsEditingUser(false)}
                className="p-2 hover:bg-slate-100 rounded-full"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label className="font-body text-sm text-slate-600">Name</Label>
                <Input
                  value={editUserData.name}
                  onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
                  className="font-body"
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm text-slate-600">Email</Label>
                <Input
                  type="email"
                  value={editUserData.email}
                  onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                  className="font-body"
                  data-testid="edit-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm text-slate-600">Phone</Label>
                <Input
                  value={editUserData.phone}
                  onChange={(e) => setEditUserData({ ...editUserData, phone: e.target.value })}
                  className="font-body"
                  data-testid="edit-user-phone"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm text-slate-600">Address</Label>
                <Textarea
                  value={editUserData.address}
                  onChange={(e) => setEditUserData({ ...editUserData, address: e.target.value })}
                  className="font-body resize-none"
                  rows={2}
                  data-testid="edit-user-address"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsEditingUser(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateUser}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                  data-testid="save-user-btn"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(null)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">Delete User?</h2>
              <p className="font-body text-sm text-slate-600 mb-6">
                Are you sure you want to delete <strong>{showDeleteConfirm.name}</strong>? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDeleteUser(showDeleteConfirm.id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
                  data-testid="confirm-delete-btn"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Result Modal */}
      {resetPasswordResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResetPasswordResult(null)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">Password Reset Successfully</h2>
              <p className="font-body text-sm text-slate-600 mb-4">
                A new temporary password has been generated{resetPasswordResult.email && ` and sent to ${resetPasswordResult.email}`}.
              </p>
              <div className="bg-slate-100 rounded-xl p-4 mb-6">
                <p className="font-body text-xs text-slate-500 mb-2">Temporary Password:</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="font-mono text-lg font-bold text-slate-900">
                    {resetPasswordResult.tempPassword}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(resetPasswordResult.tempPassword)}
                    className="h-8 w-8 p-0"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="font-body text-xs text-slate-500 mb-4">
                Please share this password securely with the user. They should change it after logging in.
              </p>
              <Button
                onClick={() => setResetPasswordResult(null)}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
